import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';

function unwrapAxios(res) {
  const ok = res?.status >= 200 && res?.status < 300;
  const body = res?.data;
  let payload = body;
  if (body && typeof body === 'object' && 'data' in body) {
    payload = body.data;
  }
  return { ok, data: payload, status: res?.status ?? 0, raw: res };
}

function orgParams(orgId) {
  return orgId ? { organisation_id: orgId } : {};
}

export const analyticsService = {
  async getCatalog() {
    return unwrapAxios(await APIService.get(Apis.platformAnalyticsCatalog));
  },

  async getScope(orgId) {
    const params = new URLSearchParams(orgParams(orgId));
    const qs = params.toString();
    const url = qs ? `${Apis.platformAnalyticsScope}?${qs}` : Apis.platformAnalyticsScope;
    return unwrapAxios(await APIService.get(url));
  },

  async getForms(campaignId) {
    if (!campaignId) {
      return { ok: false, data: { forms: [] }, status: 0 };
    }
    const params = new URLSearchParams({ campaign_id: String(campaignId) });
    const url = `${Apis.platformAnalyticsForms}?${params.toString()}`;
    return unwrapAxios(await APIService.get(url));
  },

  async getInterveneAgents(orgId) {
    const params = new URLSearchParams();
    if (orgId) params.set('organisation_id', String(orgId));
    const qs = params.toString();
    const url = qs
      ? `${Apis.platformAnalyticsInterveneAgents}?${qs}`
      : Apis.platformAnalyticsInterveneAgents;
    return unwrapAxios(await APIService.get(url));
  },

  async getInterveneFieldValues(fieldId, orgId, search = '') {
    if (!fieldId) {
      return { ok: false, data: { values: [] }, status: 0 };
    }
    const params = new URLSearchParams({ field: fieldId });
    if (orgId) params.set('organisation_id', String(orgId));
    if (search) params.set('search', search);
    const url = `${Apis.platformAnalyticsInterveneFieldValues}?${params.toString()}`;
    return unwrapAxios(await APIService.get(url));
  },

  async getFieldValues(fieldId, campaignIds = [], leadsFormIds = []) {
    if (!fieldId || !campaignIds.length) {
      return { ok: false, data: { values: [] }, status: 0 };
    }
    const params = new URLSearchParams({ field: fieldId });
    campaignIds.forEach((id) => params.append('campaign_id', id));
    leadsFormIds.filter(Boolean).forEach((id) => params.append('leads_form_id', id));
    const url = `${Apis.platformAnalyticsFormFieldValues}?${params.toString()}`;
    return unwrapAxios(await APIService.get(url));
  },

  async getFormFields(campaignIds = [], leadsFormIds = []) {
    const params = new URLSearchParams();
    campaignIds.forEach((id) => params.append('campaign_id', id));
    leadsFormIds.filter(Boolean).forEach((id) => params.append('leads_form_id', id));
    const qs = params.toString();
    const url = qs ? `${Apis.platformAnalyticsFormFields}?${qs}` : Apis.platformAnalyticsFormFields;
    return unwrapAxios(await APIService.get(url));
  },

  async previewQuery(querySpec, orgId) {
    try {
      const targetOrg = orgId || querySpec?.organisation_id;
      const endpoint = querySpec?.dataset === 'intervene'
        ? Apis.platformAnalyticsInterveneSummary
        : Apis.platformAnalyticsQueryPreview;
      return unwrapAxios(
        await APIService.post(endpoint, {
          query_spec: querySpec,
          ...orgParams(targetOrg),
        })
      );
    } catch (err) {
      return {
        ok: false,
        data: err?.response?.data || null,
        status: err?.response?.status ?? 0,
        error: err,
      };
    }
  },

  async runQuery(querySpec, orgId) {
    try {
      const targetOrg = orgId || querySpec?.organisation_id;
      const endpoint = querySpec?.dataset === 'intervene'
        ? Apis.platformAnalyticsInterveneSummary
        : Apis.platformAnalyticsQuery;
      return unwrapAxios(
        await APIService.post(endpoint, {
          query_spec: querySpec,
          ...orgParams(targetOrg),
        })
      );
    } catch (err) {
      return {
        ok: false,
        data: err?.response?.data || null,
        status: err?.response?.status ?? 0,
        error: err,
      };
    }
  },

  async getInterveneAgentSessions(querySpec, {
    responseAgentUsername = '',
    agentUserId = '',
    phoneNumber = '',
    limit,
    cursor,
    paginated = false,
  } = {}, orgId) {
    try {
      const targetOrg = orgId || querySpec?.organisation_id;
      return unwrapAxios(
        await APIService.post(Apis.platformAnalyticsInterveneAgentSessions, {
          query_spec: querySpec,
          response_agent_username: responseAgentUsername || undefined,
          agent_user_id: agentUserId || undefined,
          phone_number: phoneNumber || undefined,
          limit,
          cursor: cursor || undefined,
          paginated,
          ...orgParams(targetOrg),
        })
      );
    } catch (err) {
      return {
        ok: false,
        data: err?.response?.data || null,
        status: err?.response?.status ?? 0,
        error: err,
      };
    }
  },

  async getInterveneSessionReplies(querySpec, { sessionId, limit, cursor } = {}, orgId) {
    try {
      const targetOrg = orgId || querySpec?.organisation_id;
      return unwrapAxios(
        await APIService.post(Apis.platformAnalyticsInterveneSessionReplies, {
          query_spec: querySpec,
          session_id: sessionId,
          limit,
          cursor: cursor || undefined,
          ...orgParams(targetOrg),
        })
      );
    } catch (err) {
      return {
        ok: false,
        data: err?.response?.data || null,
        status: err?.response?.status ?? 0,
        error: err,
      };
    }
  },

  async getDashboard(orgId) {
    const params = new URLSearchParams(orgParams(orgId));
    const qs = params.toString();
    const url = qs ? `${Apis.platformAnalyticsDashboard}?${qs}` : Apis.platformAnalyticsDashboard;
    return unwrapAxios(await APIService.get(url));
  },

  async listSavedReports(orgId) {
    const params = new URLSearchParams(orgParams(orgId));
    const qs = params.toString();
    const url = qs ? `${Apis.platformAnalyticsSavedReports}?${qs}` : Apis.platformAnalyticsSavedReports;
    return unwrapAxios(await APIService.get(url));
  },

  async createSavedReport(payload, orgId) {
    return unwrapAxios(
      await APIService.post(Apis.platformAnalyticsSavedReports, {
        ...payload,
        org_id: orgId,
      })
    );
  },

  async getSavedReport(reportId, orgId) {
    try {
      const params = new URLSearchParams(orgParams(orgId));
      const qs = params.toString();
      const url = qs
        ? `${Apis.platformAnalyticsSavedReport(reportId)}?${qs}`
        : Apis.platformAnalyticsSavedReport(reportId);
      return unwrapAxios(await APIService.get(url));
    } catch (err) {
      return {
        ok: false,
        data: err?.response?.data || null,
        status: err?.response?.status ?? 0,
        error: err,
      };
    }
  },

  async updateSavedReport(reportId, payload, orgId) {
    return unwrapAxios(
      await APIService.patch(Apis.platformAnalyticsSavedReport(reportId), {
        ...payload,
        org_id: orgId,
      })
    );
  },

  async deleteSavedReport(reportId, orgId) {
    const params = new URLSearchParams(orgParams(orgId));
    const qs = params.toString();
    const url = qs
      ? `${Apis.platformAnalyticsSavedReport(reportId)}?${qs}`
      : Apis.platformAnalyticsSavedReport(reportId);
    return unwrapAxios(await APIService.remove(url));
  },

  async pinReport(reportId, orgId) {
    return unwrapAxios(
      await APIService.post(Apis.platformAnalyticsPin, {
        report_id: reportId,
        org_id: orgId,
      })
    );
  },

  async reorderPins(reportIds, orgId) {
    return unwrapAxios(
      await APIService.patch(Apis.platformAnalyticsDashboard, {
        report_ids: reportIds,
        org_id: orgId,
      })
    );
  },

  async unpinReport(reportId, orgId) {
    const params = new URLSearchParams(orgParams(orgId));
    const qs = params.toString();
    const url = qs
      ? `${Apis.platformAnalyticsUnpin(reportId)}?${qs}`
      : Apis.platformAnalyticsUnpin(reportId);
    return unwrapAxios(await APIService.remove(url));
  },

  async getTemplates(orgId) {
    const params = new URLSearchParams(orgParams(orgId));
    const qs = params.toString();
    const url = qs
      ? `${Apis.platformAnalyticsTemplates}?${qs}`
      : Apis.platformAnalyticsTemplates;
    return unwrapAxios(await APIService.get(url));
  },

  async downloadExport(querySpec, orgId) {
    const params = new URLSearchParams({
      query_spec: JSON.stringify(querySpec),
      ...orgParams(orgId),
    });
    const res = await APIService.get(
      `${Apis.platformAnalyticsExport}?${params.toString()}`,
      { responseType: 'blob' },
    );
    const blob = res?.data;
    const type = String(blob?.type || '');
    if (!blob || res.status >= 400 || type.includes('json')) {
      throw new Error('export failed');
    }
    const file = blob instanceof Blob ? blob : new Blob([blob], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'analytics-export.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
